/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios'
import { useFormik } from 'formik'
import { useRouter, useSearchParams } from 'next/navigation'
import { ChangeEvent, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { getEncryptedCookies } from '@/libs/actions/encrypt-decrypt-cookies'
import { getQueryInfo } from '@/libs/apis/queries/use-suspense-get-query-info'
import { MOENGAGE_EVENT_NAME, trackMoengageEvent } from '@/libs/configs/moengage'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import usePhoneVerify from '@/libs/hooks/use-phone-verify'
import useToast from '@/libs/hooks/use-toast'
import validateLoginForm from '@/libs/logics/validate-login-form'
import { setAccountDeletion, setSuspend } from '@/libs/store/slice/ui-slice'
import { setCurrentUserUsername } from '@/libs/store/slice/user-slice'

const useLoginForm = () => {
  const toast = useToast()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const searchParams = useSearchParams()
  const partnerId = searchParams.get('partner_id')

  const { ref, inView } = useInView()
  const [isFetching, setIsFetching] = useState(false)
  const [isTracked, setIsTracked] = useState(false)

  const { onGetOtp } = usePhoneVerify()

  const checkUser = async (username: string) => {
    try {
      setIsFetching(true)
      const { data } = await getQueryInfo(username)

      const isRegistered = data?.result?.isRegistered
      const phoneNumberVerified = data?.result?.phoneNumberVerified
      const isPINSetup = data?.result?.isPINSetup

      const linkedPnir = data?.result?.linked?.pnir || []
      const linkedPir = data?.result?.linked?.pir || []
      const hasNPRAccount = data?.result?.linked?.isNpr

      const unassign = linkedPir?.length === 0 && linkedPir.length === 0 && !hasNPRAccount
      const encryptedCookie = await getEncryptedCookies()

      const idPartner = partnerId || encryptedCookie?.partnerId

      const isPartnerToNpr =
        !hasNPRAccount && (linkedPir?.length > 0 || linkedPnir.length > 0) && !idPartner

      const isBanned = data?.result?.isBanned

      const isDeleteRequested = data?.result?.isDeleteRequested

      if (isDeleteRequested) {
        dispatch(setAccountDeletion(true))
      }

      if (isBanned) {
        dispatch(setSuspend(true))
        return router.push('/auth/suspend')
      }

      if (!isRegistered) {
        setIsFetching(false)
        return formik.setErrors({
          username:
            'No account found with the provided email or phone number. Please register or check your input again.',
        })
      }

      trackMoengageEvent(MOENGAGE_EVENT_NAME.SUBMIT_LOGIN_INFORMATION)

      if (isRegistered && (!phoneNumberVerified || !isPINSetup)) {
        let url = '/auth/register/phone-verify'

        if (partnerId) {
          url += `?partner_id=${partnerId}`
        }
        setIsFetching(false)

        return onGetOtp({
          username,
          onSuccess: () => {
            dispatch(setCurrentUserUsername(username))
            router.push(url)
          },
          onRateLimitExceeded: () => router.push(url),
        })
      }

      dispatch(setCurrentUserUsername(username))

      if (isPartnerToNpr) {
        return router.push('/auth/login/activate')
      }

      if (!!idPartner) {
        const isLinkedPnir = linkedPnir.includes(idPartner)
        const isLinkedPir = linkedPir.includes(idPartner)

        if (!isLinkedPnir && !isLinkedPir) {
          return router.push(
            hasNPRAccount || unassign ? '/auth/login/activate-partner' : '/auth/login/activate',
          )
        }
      }

      return router.push(`/auth/login/verify-pin${partnerId ? `?partner_id=${partnerId}` : ''}`)
    } catch (error) {
      setIsFetching(false)
      if (axios.isAxiosError(error)) {
        toast.show({
          title: error?.response?.data?.error?.code,
          description: error?.response?.data?.error?.message,
          traceId: error?.response?.data?.traceId || '',
          type: 'error',
        })
      } else {
        toast.show({
          title: 'Error Occured',
          description: 'Ooops something went wrong.',
          type: 'error',
        })
      }
    }
  }

  const formik = useFormik({
    initialValues: {
      username: '',
    },
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values) => validateLoginForm(values),
    onSubmit: (values) => {
      checkUser(values?.username)
    },
  })

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e)
    formik.setErrors({ ...formik.errors, username: '' })
  }

  useEffect(() => {
    if (inView && !isTracked) {
      setIsTracked(true)
      trackMoengageEvent(MOENGAGE_EVENT_NAME.VIEW_LOGIN)
    }
  }, [inView, isTracked])

  return {
    formik,
    handleInputChange,
    ref,
    isFetching,
    checkUser,
  }
}

export default useLoginForm
